import React, { useEffect, useRef, useState } from "react";
import { FiPlay, FiTrash2, FiScissors, FiGitMerge, FiPlus, FiAlertTriangle } from "react-icons/fi";
import { editTime, mergeCues, newId, readTime, splitCue, warningFor } from "../../video/subs/cues";

const inputClass = "w-full px-3 py-2 rounded-lg bg-navy border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light";

// A time that can be typed: it is only taken when it reads as a time.
function TimeField({ label, value, onCommit }) {
  const [text, setText] = useState(editTime(value));
  const [bad, setBad] = useState(false);
  useEffect(() => { setText(editTime(value)); setBad(false); }, [value]);
  const commit = () => {
    const t = readTime(text);
    if (t === null) { setBad(true); return; }
    setBad(false);
    if (Math.abs(t - value) > 0.0005) onCommit(t); else setText(editTime(value));
  };
  return (
    <input aria-label={label} value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className={`${inputClass} !w-[6.2rem] tabular-nums text-center ${bad ? "!border-red-400" : ""}`} inputMode="decimal" />
  );
}

// The list of subtitles: every cue has its times and its words, and can be played, split, joined with the next one or removed.
export default function CueEditor({ cues, onChange, activeId, onPlay, translated, limits, rtl, nowTime }) {
  const activeRef = useRef(null);
  useEffect(() => { if (activeRef.current) activeRef.current.scrollIntoView({ block: "nearest" }); }, [activeId]);
  const update = (i, patch) => onChange(cues.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const remove = (i) => onChange(cues.filter((_, k) => k !== i));
  const split = (i) => onChange([...cues.slice(0, i), ...splitCue(cues[i], null), ...cues.slice(i + 1)]);
  const merge = (i) => onChange([...cues.slice(0, i), mergeCues(cues[i], cues[i + 1]), ...cues.slice(i + 2)]);
  const add = () => {
    const at = Math.max(0, nowTime || 0);
    const k = cues.findIndex((c) => c.start > at);
    const room = k < 0 ? 2.5 : Math.min(2.5, cues[k].start - at - 0.04);
    const fresh = { id: newId(), start: at, end: at + Math.max(0.5, room), text: "New subtitle", alt: "" };
    onChange(k < 0 ? [...cues, fresh] : [...cues.slice(0, k), fresh, ...cues.slice(k)]);
  };
  return (
    <div>
      <ol className="max-h-[520px] overflow-y-auto pr-1 space-y-2.5" aria-label="Subtitles">
        {cues.map((cue, i) => {
          const warning = warningFor(cue, limits);
          const active = cue.id === activeId;
          return (
            <li key={cue.id} ref={active ? activeRef : null} className={`rounded-xl border p-3 transition-colors ${active ? "border-accent-light bg-accent/10" : "border-navy-border bg-navy/60"}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="w-6 h-6 rounded-full bg-white/10 text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <button type="button" onClick={() => onPlay(cue)} aria-label={`Play subtitle ${i + 1}`} className="w-7 h-7 rounded-full border border-white/20 hover:border-accent-light flex items-center justify-center"><FiPlay size={12} /></button>
                <TimeField label={`Subtitle ${i + 1} starts`} value={cue.start} onCommit={(t) => update(i, { start: t, end: Math.max(cue.end, t + 0.2) })} />
                <span className="text-gray-500 text-xs">to</span>
                <TimeField label={`Subtitle ${i + 1} ends`} value={cue.end} onCommit={(t) => update(i, { end: Math.max(t, cue.start + 0.2) })} />
                <span className="ms-auto flex items-center gap-1">
                  <button type="button" onClick={() => split(i)} title="Split in two" aria-label={`Split subtitle ${i + 1}`} className="p-1.5 text-gray-400 hover:text-white"><FiScissors size={14} /></button>
                  <button type="button" onClick={() => merge(i)} disabled={i === cues.length - 1} title="Join with the next one" aria-label={`Join subtitle ${i + 1} with the next`} className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"><FiGitMerge size={14} /></button>
                  <button type="button" onClick={() => remove(i)} title="Delete" aria-label={`Delete subtitle ${i + 1}`} className="p-1.5 text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>
                </span>
              </div>
              <textarea aria-label={`Subtitle ${i + 1} text`} rows={2} value={cue.text} dir={rtl ? "rtl" : "auto"} onChange={(e) => update(i, { text: e.target.value })} className={`${inputClass} resize-y leading-snug`} />
              {translated && <textarea aria-label={`Subtitle ${i + 1} translation`} rows={2} value={cue.alt} dir="auto" onChange={(e) => update(i, { alt: e.target.value })} placeholder="Translation" className={`${inputClass} resize-y leading-snug mt-2 !border-accent-light/30`} />}
              {warning && <p className="text-[11px] text-amber-300 mt-1.5 flex items-center gap-1.5"><FiAlertTriangle size={11} /> {warning}</p>}
            </li>
          );
        })}
      </ol>
      <button type="button" onClick={add} className="mt-3 text-xs text-accent-light hover:underline inline-flex items-center gap-1"><FiPlus size={13} /> Add a subtitle at the current time</button>
    </div>
  );
}
